// src/data/appealLadder.js
//
// THE APPEAL LADDER - master list 3.1, closing the first half of ISS-39. Ruling 177 (2026-09-17),
// taken under PD-19 and withdrawable by number.
//
// ---------------------------------------------------------------------------------------------
// WHY THIS IS A SECOND AXIS AND NOT MORE RUNGS ON claimLadder.js.
//
// ISS-39 states the distinction and it is the whole design: "Elevation moves a claim between
// offices. PRESENTMENT MOVES IT OFF THE PROPERTY." Every status in src\data\claimLadder.js - draft,
// submitted, intake, investigation, documentation_review, gc_review, valuation, negotiation,
// settlement, closure - describes what the ORGANIZATION is doing. Not one of them says what the
// CARRIER has received, answered, or let lapse. Adding rungs would conflate two different things
// and would put the carrier's clock inside a ladder that only the Organization can move.
//
// So this module is ACTS WITH DATES, exactly as src\data\hearingEvents.js is for the discipline
// path, and the two ladders run beside each other on one claim.
//
// ---------------------------------------------------------------------------------------------
// EVERY STEP BELOW IS A CATALOGUED PROVISION, NOT A DESIGN IDEA (PD-4).
//
// Read out of data\YCP_VIOLATION_RULE_MAP.csv on 2026-09-17 - these violation_ids exist, with
// verbatim quotes at cited lines that VIO-1 proves every run:
//
//   presentment                CLAIM-presentment (7 rows), CLAIM-presentment-time-limits,
//                              CLAIM-presentment-continuing-violation, CLAIM-presentment-documentation
//   the carrier's answer       CLAIM-carrier-response-limits (13 rows)
//   silence                    CLAIM-carrier-response-limits-default-allowance (2 rows)
//   the appeal up              CLAIM-successive-officer-handling, CLAIM-time-limits-appeal-listing
//   the conference             CLAIM-highest-officer-conference, -obligation,
//                              CLAIM-monthly-conference-obligation, CLAIM-refusal-of-conference
//   to a Board                 CLAIM-joint-submission-preparation, CLAIM-ex-parte-submission-preparation,
//                              CLAIM-restriction-of-arbitral-submission
//   after the award            CLAIM-failure-to-comply-with-award
//
// The ladder is those provisions in the order the agreements put them. Nothing here was invented to
// fill a gap between two of them.
//
// ---------------------------------------------------------------------------------------------
// THE RULE THAT GOVERNS EVERY CLOCK IN THIS FILE, AND IT IS THE REASON MOST OF IT REFUSES.
//
// A response limit is a NUMBER IN AN AGREEMENT. This platform has 13 catalogued rows about carrier
// response limits and it has NOT extracted a day-count from any of them - the quotes carry the
// numbers, the map does not. src\data\claimRequirements.js carries filingWindowDays: 60 for two
// agreements, and that is the ON-PROPERTY FILING window (Article 20(a) / Rule 29(a)), which is a
// different clock from the carrier's time to answer.
//
// So: THIS MODULE NEVER SUPPLIES A DEFAULT. Given no recorded limit for an agreement it returns
// UNKNOWN and says which provision would carry the number. It does not fall back to 60 because 60 is
// the number that happens to be nearby, and a deadline computed from the wrong clock is worse than
// no deadline - it would be shown to an officer who would rely on it. This is deskQueue.js's rule
// ("an unknown is never rendered as a zero and never as a safe value") applied to the carrier.
//
// Pure module: no React, no Firestore. The page draws and writes; this decides.
import { lastDayOf } from './timeLimit';   // ruling 314: day one is the day of the event
import { signedByOf } from './appealSigner';   // master list item 4: the one who submits signs

const F = Object.freeze;

// ---------------------------------------------------------------------------------------------
// THE ACTS

export const SIDES = F(['organization', 'carrier']);

export const ACTS = F({
  presented:           F({ side: 'organization', label: 'Claim presented on the carrier', needs: ['servedOn', 'means'], starts: 'carrier_answer' }),
  carrier_answered:    F({ side: 'carrier',      label: 'Carrier answered',               needs: ['outcome'] }),
  appealed:            F({ side: 'organization', label: 'Appealed to the next officer',   needs: ['toOfficer'], starts: 'carrier_answer' }),
  conference_requested:F({ side: 'organization', label: 'Conference requested',           needs: [] }),
  conference_held:     F({ side: 'carrier',      label: 'Conference held',                needs: [] }),
  conference_refused:  F({ side: 'carrier',      label: 'Conference refused',             needs: [] }),
  conference_decided:  F({ side: 'carrier',      label: 'Decision rendered after conference', needs: ['outcome'] }),   // §415: the carrier's written decision after the monthly conference - its own act (Bob 2026-09-25)
  board_notice:        F({ side: 'organization', label: 'Notice of intent to the Board',  needs: ['board'] }),
  board_submitted:     F({ side: 'organization', label: 'Submission filed with the Board', needs: ['board', 'submissionKind'] }),
  award_issued:        F({ side: 'carrier',      label: 'Award issued',                   needs: ['awardOutcome'] }),
  award_complied:      F({ side: 'carrier',      label: 'Award complied with',            needs: [] }),
  award_not_complied:  F({ side: 'organization', label: 'Carrier did not comply with the award', needs: [] }),
  dissent_filed:       F({ side: 'organization', label: 'Labor member dissent filed to the award', needs: [] }),   // §416: when we disagree with the neutral (Bob 2026-09-25)
  extended:            F({ side: 'organization', label: 'Time limit extended by agreement', needs: ['agreedWith'] }),   // Record 587 (ruling 267 audit): newDue only when granted   // ruling 267 (2026-10-02): an extension is a recorded fact - new due date, who agreed, how
});

export const ACT_KINDS = F(Object.keys(ACTS));

export const ANSWER_OUTCOMES = F(['denied', 'allowed', 'allowed_in_part', 'no_answer_on_record']);
export const SUBMISSION_KINDS = F(['joint', 'ex_parte']);          // CLAIM-joint- / -ex-parte-submission-preparation
export const AWARD_OUTCOMES = F(['sustained', 'denied', 'dismissed', 'sustained_in_part']);

// Ruling 268 (2026-10-02): ONE OUTCOME BOX per step's answer - Granted / Partially granted and denied / Denied -
// with the amount allowed where money is involved. The box is the EXISTING closed set ANSWER_OUTCOMES (ruling
// 177 / §415), shown under the ruling's own words; no key is renamed, so every stored act and every reader
// (claimClocks /denied/, warRoomBoard /^allowed/) keeps working. `no_answer_on_record` stays: it is not an
// answer and is never counted in one of the three boxes.
// Withdrawable by number: set RULING_268_OUTCOME_BOX false - labels fall back to the keys and amountAllowed is
// neither taken nor written.
export const RULING_268_OUTCOME_BOX = true;
export const ANSWER_BOX = F([
  F({ key: 'allowed',             label: 'Granted',                      box: 'granted', money: true }),
  F({ key: 'allowed_in_part',     label: 'Partially granted and denied', box: 'partly',  money: true }),
  F({ key: 'denied',              label: 'Denied',                       box: 'denied',  money: false }),
  F({ key: 'no_answer_on_record', label: 'No answer on record',          box: null,      money: false }),
]);
export function answerBoxLabel(outcome) {
  const k = str(outcome);
  if (!RULING_268_OUTCOME_BOX) return k.replace(/_/g, ' ');
  const m = ANSWER_BOX.find(b => b.key === k);
  return m ? m.label : k.replace(/_/g, ' ');
}
// Which of the three boxes an answer falls in: 'granted' | 'partly' | 'denied' | null (no answer / unknown).
export function answerBox(outcome) {
  const m = ANSWER_BOX.find(b => b.key === str(outcome));
  return m ? m.box : null;
}
// Ruling 268: the amount allowed is OPTIONAL. Blank -> null: nobody recorded a figure, UNKNOWN and never zero (the
// rule claimOutcome.js holds for amountRecovered). Taken only on Granted / Partially granted; on Denied or no answer
// it is refused, because an amount allowed on a denial contradicts the record. Returns undefined when the ruling is off.
function amountAllowedOf(i, outcome, errors) {
  if (!RULING_268_OUTCOME_BOX) return undefined;
  const raw = i.amountAllowed;
  if (raw === undefined || raw === null || String(raw).trim() === '') return null;
  const n = Number(String(raw).replace(/[$,\s]/g, ''));
  if (!Number.isFinite(n) || n < 0) { errors.push('The amount allowed is a number of dollars, or left blank. Blank means no figure was recorded; zero means nothing was allowed.'); return null; }
  const m = ANSWER_BOX.find(b => b.key === outcome);
  if (!m || !m.money) { errors.push('An amount allowed is recorded only on an answer that granted the claim in whole or in part.'); return null; }
  return Math.round(n * 100) / 100;
}

function str(v) { return typeof v === 'string' ? v.trim() : ''; }
function isDate(s) { return typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s) && !Number.isNaN(Date.parse(s + 'T00:00:00Z')); }
function day(s) { return Date.parse(s + 'T00:00:00Z'); }

// ---------------------------------------------------------------------------------------------
// RECORDING AN ACT

export function recordAct(input) {
  const i = input || {};
  const kind = str(i.kind);
  const errors = [];
  const spec = ACTS[kind];
  if (!spec) return { ok: false, errors: ['"' + kind + '" is not an act on the appeal ladder.'], record: null };
  if (!isDate(i.on)) errors.push('An act carries the date it happened, as YYYY-MM-DD.');

  const rec = { kind, side: spec.side, on: str(i.on), note: str(i.note) || null };

  if (kind === 'presented') {
    // ISS-39 point 2: the record of SERVICE is the fact that starts the carrier's clock, and the
    // Organization will be held to the same standard it is enforcing. So it is required, not optional.
    rec.servedOn = str(i.servedOn);
    rec.means = str(i.means);
    if (!rec.servedOn) errors.push('Service names the officer or office it was served on. The claim that cannot say who received it cannot prove the carrier’s clock started.');
    if (!rec.means) errors.push('Service names how it was served - the agreements test written or electronic notice.');
  }
  if (kind === 'carrier_answered') {
    rec.outcome = str(i.outcome);
    if (!ANSWER_OUTCOMES.includes(rec.outcome)) errors.push('An answer is one of: ' + ANSWER_OUTCOMES.join(', ') + '.');
    rec.reasonGiven = str(i.reasonGiven) || null;
    const amt = amountAllowedOf(i, rec.outcome, errors);   // ruling 268
    if (amt !== undefined) rec.amountAllowed = amt;
  }
  if (kind === 'conference_decided') {
    rec.outcome = str(i.outcome);
    if (!ANSWER_OUTCOMES.includes(rec.outcome)) errors.push('A conference decision is one of: ' + ANSWER_OUTCOMES.join(', ') + '.');
    rec.reasonGiven = str(i.reasonGiven) || null;
    const amt = amountAllowedOf(i, rec.outcome, errors);   // ruling 268
    if (amt !== undefined) rec.amountAllowed = amt;
  }
  if (kind === 'appealed') {
    rec.toOfficer = str(i.toOfficer);
    if (!rec.toOfficer) errors.push('An appeal names the officer it went to (CLAIM-successive-officer-handling).');
    // Forensic audit 2026-10-16: the day the CARRIER RECEIVED (receipted) the appeal, optional. CSX answers an appeal
    // "within seventy-five (75) calendar days from the date appeal was receipted" (csx-2002-system-agreement.source.txt:
    // 1996) - not from the date it was sent. Blank stays absent; answerDue then counts from `on` and says so.
    if (str(i.receivedOn)) {
      if (!isDate(str(i.receivedOn))) errors.push('The date the carrier received the appeal is YYYY-MM-DD, or blank.');
      else if (isDate(i.on) && day(str(i.receivedOn)) < day(str(i.on))) errors.push('The carrier cannot have received the appeal before it was sent.');
      else rec.receivedOn = str(i.receivedOn);
    }
  }
  if (kind === 'board_notice' || kind === 'board_submitted') {
    rec.board = str(i.board);
    if (!rec.board) errors.push('Name the Board.');
  }
  // Master list item 4 (Bob 2026-10-14): the one who submits signs - the appeal and the notice to a Board carry their
  // signer, apart from whoever typed the record. Optional on the input so an older caller still records; the officer's
  // panel always sends it (OffPropertyPanel, appealSigner.js).
  if ((kind === 'appealed' || kind === 'board_notice') && i.signer) {
    const sg = signedByOf(i.signer);
    if (!sg) errors.push('The appeal is signed by the one who submits it - the signer needs a name and an office.');
    else rec.signedBy = sg;
  }
  if (kind === 'board_submitted') {
    rec.submissionKind = str(i.submissionKind);
    if (!SUBMISSION_KINDS.includes(rec.submissionKind)) errors.push('A submission is joint or ex parte.');
    rec.docketNumber = str(i.docketNumber) || null;
  }
  if (kind === 'award_issued') {
    rec.awardOutcome = str(i.awardOutcome);
    if (!AWARD_OUTCOMES.includes(rec.awardOutcome)) errors.push('An award outcome is one of: ' + AWARD_OUTCOMES.join(', ') + '.');
    rec.awardRef = str(i.awardRef) || null;
  }
  if (kind === 'extended') {
    // Ruling 267: the agreements let the parties extend a time limit BY AGREEMENT (e.g. Amtrak Rule 29,
    // CLAIM-repudiation-of-agreed-extension). An extension not recorded here is not shown as one.
    // Record 587 (ruling 267 audit): granted or not - a yes/no fact. `granted` defaults to true. A request
    // that was NOT granted carries no new due date (one supplied is refused) and never moves the clock.
    rec.granted = i.granted !== false;
    rec.agreedWith = str(i.agreedWith);
    rec.agreedBy = str(i.agreedBy) || null;
    rec.how = str(i.how) || null;
    if (rec.granted) {
      rec.newDue = str(i.newDue);
      if (!isDate(rec.newDue)) errors.push('An extension carries the new due date, as YYYY-MM-DD.');
      else if (isDate(i.on) && day(rec.newDue) <= day(str(i.on))) errors.push('The new due date must be after the date the extension was agreed.');
      if (!rec.agreedWith) errors.push('An extension names the carrier officer who agreed to it.');
    } else {
      if (str(i.newDue)) errors.push('An extension that was not granted carries no new due date.');
      if (!rec.agreedWith) errors.push('A refused extension names the carrier officer who refused it.');
    }
  }
  if (kind === 'dissent_filed') {
    // A dissent is the labor member's recorded disagreement with the neutral's award. Its basis
    // travels as the note; nothing else is required beyond the date the shared check already tests.
    rec.awardRef = str(i.awardRef) || null;
  }

  if (errors.length) return { ok: false, errors, record: null };
  return { ok: true, errors: [], record: rec };
}

export function sortActs(acts) {
  const list = (Array.isArray(acts) ? acts : []).filter(a => a && ACTS[a.kind]);
  const dated = list.filter(a => isDate(a.on)).sort((a, b) => day(a.on) - day(b.on));
  const undated = list.filter(a => !isDate(a.on));
  return dated.concat(undated);     // an undated act sorts LAST, never first (deskQueue's rule)
}

export function lastOf(acts, kind) {
  const m = sortActs(acts).filter(a => a.kind === kind);
  return m.length ? m[m.length - 1] : null;
}

// ---------------------------------------------------------------------------------------------
// THE CARRIER'S CLOCK - and its refusal

// `limits` is what the agreement records, NOT what this file thinks. Shape:
//   { carrierAnswerDays: <number|null>, provision: '<violation_id>' }
// A null day-count is the normal case today: 13 rows are catalogued and not one has had its number
// extracted. Passing nothing at all is the same as passing null.
export function answerDue(acts, limits) {
  const l = limits || {};
  const started = lastOf(acts, 'appealed') || lastOf(acts, 'presented');
  if (!started || !isDate(started.on)) {
    return { state: 'not_presented', due: null, reason: 'The claim has not been presented on the carrier, so no carrier clock is running.' };
  }
  const answered = lastOf(acts, 'carrier_answered');
  if (answered && isDate(answered.on) && day(answered.on) >= day(started.on)) {
    return { state: 'answered', due: null, on: answered.on, outcome: answered.outcome, reason: null };
  }
  // §415 (2026-09-25): the appeal clock can differ from the initial one. CSX answers a PRESENTED
  // claim in 60 days and an APPEAL in 75 (data\YCP_CARRIER_ANSWER_CLOCKS_2026-09-25.md, confirmed).
  // When the running clock started with an `appealed` act AND the agreement records a DISTINCT appeal
  // figure, use it; otherwise the initial figure stands - the agreements' successive-officer clause
  // (CLAIM-successive-officer-handling) recurs the same limit at each appeal step, so falling back to
  // the initial number is the agreement's own rule, not a guess. A null initial stays "unknown".
  // Ruling 267: the latest extension recorded on or after the running clock started replaces the due date.
  // Record 587 (ruling 267 audit): a refused extension (granted === false) never moves the due date.
  const ext = (sortActs(acts).filter(a => a.kind === 'extended' && a.granted !== false && isDate(a.on) && isDate(a.newDue)
    && day(a.on) >= day(started.on)).pop()) || null;
  if (ext) {
    return { state: 'running', due: ext.newDue, since: started.on, days: null, extendedOn: ext.on,
             extendedWith: ext.agreedWith || null, reason: null };
  }
  // Forensic audit 2026-10-16: where the agreement states NO carrier answer clock on appeal (appealAnswerStated false -
  // Amtrak 29(f), Conrail 4-K-1(f), Conrail M/S 14(A), Soo Line 25), nothing recurs: no date, no default on appeal.
  if (started.kind === 'appealed' && l.appealAnswerStated === false && !Number.isFinite(l.appealAnswerDays)) {
    return {
      state: 'unknown',
      due: null,
      since: started.on,
      notStated: true,
      reason: 'This agreement states no time for the carrier to answer an appeal, so no date is computed and no default is '
            + 'declared on appeal. Where the agreement sets a decision clock from the date the claim is discussed in '
            + 'conference, record the conference: that clock runs from it.',
    };
  }
  const days = (started.kind === 'appealed' && Number.isFinite(l.appealAnswerDays))
    ? l.appealAnswerDays
    : (Number.isFinite(l.carrierAnswerDays) ? l.carrierAnswerDays : null);
  if (days === null) {
    return {
      state: 'unknown',
      due: null,
      since: started.on,
      reason: 'The carrier’s time to answer is not on record for this agreement, so no date is computed. '
            + 'The provision that carries it is ' + (str(l.provision) || 'CLAIM-carrier-response-limits')
            + '; it is catalogued with a verbatim quote and its day-count has never been extracted. '
            + 'A date guessed from another clock would be worse than none.',
    };
  }
  // Forensic audit 2026-10-16 (CSX Article 20(d), csx-2002-system-agreement.source.txt:1996: "from the date appeal was
  // receipted"): where the agreement counts the appeal answer from RECEIPT (limits.appealAnswerFrom === 'receipt'), the
  // clock runs from the appeal act's receivedOn. Not recorded: counted from the appeal date - the earliest the receipt
  // can be, so the date shown is never later than the true one - and the result says the receipt is not recorded.
  if (started.kind === 'appealed' && l.appealAnswerFrom === 'receipt' && Number.isFinite(l.appealAnswerDays) && days === l.appealAnswerDays) {
    if (isDate(started.receivedOn)) {
      return { state: 'running', due: lastDayOf(started.receivedOn, days), since: started.receivedOn, days, countedFrom: 'receipt', reason: null };
    }
    return { state: 'running', due: lastDayOf(started.on, days), since: started.on, days, countedFrom: 'appeal', receiptNotRecorded: true,
      reason: null, note: 'The agreement counts this answer from the date the carrier receipted the appeal, which is not recorded - counted here from the appeal date, the earliest the receipt can be. Record the receipt date to count from it.' };
  }
  return { state: 'running', due: lastDayOf(started.on, days), since: started.on, days, reason: null };   // ruling 314
}

// Record 612 (Bob 10/8, decision 28 "agreed"): ONE WORDING FOR A LAPSED CARRIER ANSWER. The off-property panel's
// silence watch (below) and the default banner (carrierDefault.bannerText) say the same sentence, from here:
//   "Under <rule> the claim is allowed as presented: '<verbatim quote>'. No written denial is recorded on the platform.
//    Confirm none was received, then send the default letter."
// The quote is the agreement's own words, verbatim (PD-16), where the caller has them; where it does not, the
// provision is named and NOTHING is paraphrased in the quote's place. Retired: "never an automatic allowance".
// The quote is passed through untouched - not trimmed, not tidied - so it is the profile's text exactly.
export function lapsedAnswerWording(rule, quote) {
  const r = str(rule) || 'CLAIM-carrier-response-limits-default-allowance';
  const q = (typeof quote === 'string' && quote.trim()) ? quote : '';
  // Forensic audit 2026-10-16: with no verbatim default quote, the platform does not know the consequence the agreement
  // attaches (Conrail M/S 14(A): "allowed to the extent of monetary loss sustained", not "as presented"). It no longer
  // asserts "allowed as presented" without the agreement's words; it names the provision and says the time has run.
  if (!q) {
    return 'The carrier\'s time to answer under ' + r + ' has run. No written denial is recorded on the platform. '
      + 'Confirm none was received, read the provision for its consequence, then act on it.';
  }
  return 'Under ' + r + ' the claim is allowed as presented' + ": '" + q + "'" + '. '
    + 'No written denial is recorded on the platform. Confirm none was received, then send the default letter.';
}

// CLAIM-carrier-response-limits-default-allowance. ISS-39 point 3: "a sixty-day silence is a claim
// event, and nothing currently watches for one". This is the watch - and it refuses to fire on a
// clock it does not have, which is the honest half of watching.
export function silenceStatus(acts, limits, today) {
  const d = answerDue(acts, limits);
  if (d.state !== 'running') return { lapsed: false, ...d };
  const now = isDate(today) ? today : new Date().toISOString().slice(0, 10);
  if (day(now) <= day(d.due)) return { lapsed: false, ...d };
  return {
    lapsed: true,
    ...d,
    state: 'lapsed',
    // Record 612 (decision 28): the one wording. The expiry date travels as `due`; the panel prints it beside this.
    reason: lapsedAnswerWording((limits || {}).defaultAllowanceRule || (limits || {}).defaultAllowanceProvision,
      (limits || {}).defaultAllowanceQuote),
  };
}

// THE CONFERENCE DECISION CLOCK. §415 (Bob 2026-09-25). Some agreements (Amtrak, Conrail) require the
// carrier to render a WRITTEN DECISION within a set time FROM THE DATE THE CLAIM WAS DISCUSSED in
// conference - a different event from presentment, so it is its own act (`conference_held` starts it,
// `conference_decided` stops it) and its own clock. Like answerDue it refuses to invent a number.
// Whether missing THIS deadline itself triggers the default allowance is a separate per-agreement
// question and is NOT assumed here - this reports the deadline, not a consequence.
export function conferenceDecisionDue(acts, limits, today) {
  const l = limits || {};
  const held = lastOf(acts, 'conference_held');
  if (!held || !isDate(held.on)) {
    return { state: 'no_conference', due: null, reason: 'No conference has been held, so no conference-decision clock is running.' };
  }
  const decided = lastOf(acts, 'conference_decided');
  if (decided && isDate(decided.on) && day(decided.on) >= day(held.on)) {
    return { state: 'decided', due: null, on: decided.on, outcome: decided.outcome, reason: null };
  }
  const days = Number.isFinite(l.conferenceDecisionDays) ? l.conferenceDecisionDays : null;
  if (days === null) {
    return { state: 'unknown', due: null, since: held.on,
      reason: 'The carrier’s time to render a decision after conference is not on record for this agreement. '
            + 'The provision that carries it is ' + (str(l.provision) || 'CLAIM-carrier-response-limits') + '.' };
  }
  return { state: 'running', due: lastDayOf(held.on, days), since: held.on, days, reason: null };   // ruling 314
}

// ---------------------------------------------------------------------------------------------
// WHERE THE CLAIM STANDS OFF THE PROPERTY

export const STANDING = F({
  NOT_PRESENTED: 'not_presented',
  WITH_CARRIER: 'with_carrier',
  ANSWERED: 'answered',
  ON_APPEAL: 'on_appeal',
  AT_CONFERENCE: 'at_conference',
  AT_BOARD: 'at_board',
  AWARDED: 'awarded',
  COMPLIANCE_OPEN: 'compliance_open',
});

export function standing(acts) {
  const s = sortActs(acts);
  if (!s.length) return { key: STANDING.NOT_PRESENTED, label: 'Not presented on the carrier' };
  const has = k => s.some(a => a.kind === k);
  if (has('award_not_complied')) return { key: STANDING.COMPLIANCE_OPEN, label: 'Award not complied with (CLAIM-failure-to-comply-with-award)' };
  if (has('award_issued')) return { key: STANDING.AWARDED, label: 'Award issued' };
  if (has('board_submitted') || has('board_notice')) return { key: STANDING.AT_BOARD, label: 'Before a Board' };
  if (has('conference_requested') && !has('conference_held') && !has('conference_refused')) return { key: STANDING.AT_CONFERENCE, label: 'Conference requested' };
  if (has('appealed')) return { key: STANDING.ON_APPEAL, label: 'On appeal to a higher officer' };
  if (has('carrier_answered')) return { key: STANDING.ANSWERED, label: 'Answered by the carrier' };
  if (has('presented')) return { key: STANDING.WITH_CARRIER, label: 'With the carrier' };
  return { key: STANDING.NOT_PRESENTED, label: 'Not presented on the carrier' };
}

// The whole property-side picture for one claim, for a panel to draw and for nothing to compute
// twice. `limits` and `today` are passed in; this file reads no clock and no agreement of its own.
export function appealTimeline(acts, limits, today) {
  const s = sortActs(acts);
  const presented = lastOf(s, 'presented');
  return {
    acts: s,
    standing: standing(s),
    presented: presented ? { on: presented.on, servedOn: presented.servedOn, means: presented.means } : null,
    answer: answerDue(s, limits),
    silence: silenceStatus(s, limits, today),
    conferenceDecision: conferenceDecisionDue(s, limits, today),   // §415
    // Named rather than left to a reader to notice: an appeal ladder with no presentment is a
    // record of the Organization talking to itself.
    gaps: (!presented && s.length) ? ['Acts are recorded but the claim was never presented on the carrier.'] : [],
  };
}
